import { Navigation } from '../../components/Navigation';
import { TrendingUp, Star, Zap, Target, Check, ArrowRight, BarChart3, Users } from 'lucide-react';

export function PriorityVisibility() {
  const tiers = [
    {
      name: 'Standard',
      price: 0,
      period: 'month',
      description: 'Basic job posting',
      features: [
        'Standard job listings',
        'Basic applicant view',
        'Email notifications',
        'Standard support',
      ],
      cta: 'Current Plan',
      disabled: true,
    },
    {
      name: 'Priority',
      price: 49,
      period: 'month',
      description: 'Get noticed faster',
      popular: true,
      features: [
        'Everything in Standard, plus:',
        'Higher ranking in job feed',
        'Logo branding on cards',
        'Instant applicant summaries',
        'Slack + Outlook integrations',
        'Application prediction scoring',
        '2x more views',
      ],
      cta: 'Upgrade to Priority',
    },
    {
      name: 'Premium',
      price: 199,
      period: 'month',
      description: 'Maximum visibility',
      features: [
        'Everything in Priority, plus:',
        'Featured at top of feed',
        'Custom branding colors',
        'AI candidate matching',
        'Priority support (1-hour response)',
        'Advanced analytics dashboard',
        '5x more views',
      ],
      cta: 'Upgrade to Premium',
    },
  ];

  const benefits = [
    {
      icon: TrendingUp,
      title: 'Higher Job Rankings',
      description: 'Your jobs appear at the top of candidate feeds',
      stat: '3-5x views',
    },
    {
      icon: Star,
      title: 'Logo Branding',
      description: 'Stand out with your company logo on every job card',
      stat: '40% CTR boost',
    },
    {
      icon: Zap,
      title: 'Instant Summaries',
      description: 'AI-generated applicant summaries in seconds',
      stat: '10x faster',
    },
    {
      icon: Target,
      title: 'Prediction Scoring',
      description: 'Know which candidates are most likely to accept',
      stat: '85% accuracy',
    },
  ];

  const integrations = [
    {
      name: 'Slack Integration',
      description: 'Get instant notifications for new applications',
      icon: '💬',
    },
    {
      name: 'Outlook Calendar',
      description: 'Schedule interviews with one click',
      icon: '📅',
    },
    {
      name: 'Applicant Summaries',
      description: 'AI-powered candidate insights',
      icon: '🤖',
    },
    {
      name: 'Analytics Dashboard',
      description: 'Track views, applications, and conversions',
      icon: '📊',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Navigation />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-100 to-amber-100 px-4 py-2 rounded-full mb-6">
            <Star className="w-5 h-5 text-orange-600" />
            <span className="text-sm font-semibold text-orange-900">Employer Upgrades</span>
          </div>

          <h1 className="text-5xl sm:text-6xl font-bold text-gray-900 mb-6">
            Priority Visibility
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Your best candidates should see you first. Get noticed faster with priority job placement.
          </p>
        </div>

        <div className="grid md:grid-cols-4 gap-6 mb-16">
          {benefits.map((benefit) => {
            const Icon = benefit.icon;
            return (
              <div
                key={benefit.title}
                className="bg-white rounded-xl p-6 border-2 border-gray-200 hover:border-orange-300 transition-all hover:shadow-lg text-center"
              >
                <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl mb-4">
                  <Icon className="w-6 h-6 text-white" />
                </div>

                <div className="text-2xl font-bold text-orange-600 mb-2">
                  {benefit.stat}
                </div>

                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  {benefit.title}
                </h3>
                <p className="text-sm text-gray-600">
                  {benefit.description}
                </p>
              </div>
            );
          })}
        </div>

        <div className="grid lg:grid-cols-3 gap-8 mb-20">
          {tiers.map((tier) => {
            const isPopular = tier.popular;

            return (
              <div
                key={tier.name}
                className={`relative bg-white rounded-2xl p-8 border-2 transition-all ${
                  isPopular
                    ? 'border-orange-500 scale-105 shadow-xl'
                    : 'border-gray-200 hover:border-gray-300 hover:shadow-lg'
                }`}
              >
                {isPopular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <div className="bg-gradient-to-r from-orange-500 to-amber-500 text-white px-6 py-2 rounded-full text-sm font-bold shadow-lg">
                      Most Popular
                    </div>
                  </div>
                )}

                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{tier.name}</h3>
                  <p className="text-gray-600 mb-4">{tier.description}</p>

                  <div className="flex items-baseline justify-center gap-1 mb-2">
                    <span className="text-4xl font-bold text-gray-900">
                      ${tier.price}
                    </span>
                    <span className="text-gray-600">/{tier.period}</span>
                  </div>
                </div>

                <div className="space-y-3 mb-8">
                  {tier.features.map((feature, idx) => (
                    <div key={idx}>
                      {feature.includes('Everything') ? (
                        <div className="text-sm font-semibold text-gray-900 py-1">
                          {feature}
                        </div>
                      ) : (
                        <div className="flex items-start gap-3">
                          <Check className="w-5 h-5 flex-shrink-0 mt-0.5 text-orange-500" />
                          <span className="text-sm text-gray-700">{feature}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                <button
                  disabled={tier.disabled}
                  className={`w-full py-3 px-6 rounded-xl font-bold transition-all ${
                    tier.disabled
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : isPopular
                      ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white hover:from-orange-600 hover:to-amber-600 shadow-lg hover:shadow-xl'
                      : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                  }`}
                >
                  {tier.cta}
                </button>
              </div>
            );
          })}
        </div>

        <div className="mb-20">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Powerful Integrations Included
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {integrations.map((integration) => (
              <div
                key={integration.name}
                className="bg-white rounded-xl p-6 border-2 border-gray-200 hover:border-orange-300 transition-all"
              >
                <div className="text-4xl mb-3">{integration.icon}</div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  {integration.name}
                </h3>
                <p className="text-sm text-gray-600">
                  {integration.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-20">
          <div className="bg-gradient-to-br from-orange-500 to-amber-500 rounded-2xl p-8 text-white">
            <BarChart3 className="w-12 h-12 mb-4 opacity-90" />
            <h3 className="text-2xl font-bold mb-4">Advanced Analytics</h3>
            <p className="text-white/90 mb-6">
              Track every metric that matters with real-time dashboards
            </p>

            <div className="space-y-3">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
                <div className="text-sm text-white/80 mb-1">Job Views</div>
                <div className="text-2xl font-bold">2,847</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
                <div className="text-sm text-white/80 mb-1">Applications</div>
                <div className="text-2xl font-bold">143</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
                <div className="text-sm text-white/80 mb-1">Conversion Rate</div>
                <div className="text-2xl font-bold">5.02%</div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-8 border-2 border-gray-200">
            <Users className="w-12 h-12 mb-4 text-orange-500" />
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              AI Candidate Matching
            </h3>
            <p className="text-gray-600 mb-6">
              Get instant quality scores for every applicant
            </p>

            <div className="space-y-3">
              <div className="bg-gray-50 rounded-lg p-4 border-2 border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold text-gray-900">Sarah Johnson</span>
                  <div className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-bold">
                    95% Match
                  </div>
                </div>
                <p className="text-xs text-gray-600">
                  Strong skills match • 5 years experience • Available immediately
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 border-2 border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold text-gray-900">Michael Chen</span>
                  <div className="bg-cyan-100 text-cyan-700 px-3 py-1 rounded-full text-xs font-bold">
                    88% Match
                  </div>
                </div>
                <p className="text-xs text-gray-600">
                  Good culture fit • 3 years experience • Seeking remote
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-10 text-center text-white mb-16">
          <h2 className="text-3xl font-bold mb-4">
            Pay-Per-Hire Option Available
          </h2>
          <p className="text-xl text-white/90 max-w-3xl mx-auto mb-8">
            Not ready for a subscription? Try our pay-per-hire model. Only pay when you find the right fit. Fees range from $150-$1,500 depending on job level.
          </p>

          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
              <div className="text-3xl font-bold mb-2">$150</div>
              <p className="text-sm text-white/80">Entry-level positions</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
              <div className="text-3xl font-bold mb-2">$500</div>
              <p className="text-sm text-white/80">Mid-level positions</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
              <div className="text-3xl font-bold mb-2">$1,500</div>
              <p className="text-sm text-white/80">Senior positions</p>
            </div>
          </div>

          <button className="mt-8 px-8 py-4 bg-white text-gray-900 rounded-xl font-bold hover:bg-gray-100 transition-all inline-flex items-center gap-2">
            Learn About Pay-Per-Hire
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>

        <div className="bg-white rounded-2xl p-10 border-2 border-gray-200 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Ready to Hire Faster?
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-8">
            Start attracting top talent with Priority Visibility. Cancel anytime.
          </p>
          <div className="flex items-center justify-center gap-4">
            <button className="px-10 py-4 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-xl font-bold hover:from-orange-600 hover:to-amber-600 transition-all shadow-lg hover:shadow-xl">
              Upgrade Now
            </button>
            <button className="px-10 py-4 bg-white text-gray-900 border-2 border-gray-300 rounded-xl font-bold hover:border-orange-500 hover:text-orange-600 transition-all">
              Schedule Demo
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
